import { useState } from 'react';
import { userOptionSelected } from '../../actions/cartActions';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../common/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { ScrollArea } from '../../../../common/components/ui/scroll-area';
import { cn } from '../../../../common/utils/styleUtil';
import ProgressButton from '../../../../common/components/ProgressButton';
import {
  useSendPositionAvailabilityOptionsToGoogleAnalyticsEffect,
  useSendPositionAvailabilitySelectionsToGoogleAnalytics
} from '../../../../tracking/googleAnalytics.ts';
import { useCartIsBusy } from '../../state-hooks/useCartIsBusy';
import { useCartUserPromptNotification } from '../../state-hooks/useCartUserPromptNotification';
import { toKeyedItems } from '../../../../common/utils/keyedItems';

export function CartDialogChoosePositionAvailabilityOptions() {
  const { positionAvailabilityOptions } = useCartUserPromptNotification();
  const { t } = useTranslation();
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(
    // Anfangs-Zustand: jeweils die erste Option auswählen
    () => (positionAvailabilityOptions ? positionAvailabilityOptions.map(() => 0) : [])
  );

  const { cartIsProcessingPosition } = useCartIsBusy();

  useSendPositionAvailabilityOptionsToGoogleAnalyticsEffect(positionAvailabilityOptions);
  const sendPositionAvailabilitySelectionsToGoogleAnalytics = useSendPositionAvailabilitySelectionsToGoogleAnalytics();

  function handleOptionChange(positionIndex, optionIndex) {
    setSelectedOptionIndex(prevState => {
      const newState = [...prevState];
      newState[positionIndex] = Number.parseInt(optionIndex);
      return newState;
    });
  }

  function confirmUserOptionSelection() {
    if (positionAvailabilityOptions && selectedOptionIndex != undefined) {
      const selectedAvailabilityOptions = positionAvailabilityOptions.map(
        (position, index) => position.availabilityOptions[selectedOptionIndex[index]]
      );
      sendPositionAvailabilitySelectionsToGoogleAnalytics(selectedAvailabilityOptions);
      userOptionSelected(selectedAvailabilityOptions);
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className={'flex items-center'}>{t('Anpassungen erforderlich:')}</DialogTitle>
        <DialogDescription className={'block h-[100px]]'}>
          {t(
            'Die Positionen sind nicht wie gewünscht lieferbar, bitte entscheiden Sie sich für eine passende Lieferoption'
          )}
        </DialogDescription>
      </DialogHeader>
      <ScrollArea className="overflow-y-auto">
        {toKeyedItems(positionAvailabilityOptions ?? [], position => position.message).map(
          ({ key: positionKey, item: position }, positionIndex) => {
            const lines = position.message.split(/\n/).filter(line => line.trim() !== '');
            const formattedMessage = toKeyedItems(lines).map(({ key: lineKey, item: line }, lineIndex) => {
              let className = 'ml-2'; // mittlere Zeilen -> Beschreibung der Position (Artikel, Mengen, Preis)
              if (lineIndex === 0) {
                className = 'font-bold mt-3 mb-3 flex items-center'; // Erste Zeile -> Überschrift
              } else if (lineIndex === lines.length - 1) {
                className = 'mt-2 ml-2 mb-2'; // Letzte Zeile -> "Sie haben folgende Möglichkeiten" -> Abstand nach oben
              }
              return (
                <div key={lineKey} className={className}>
                  {lineIndex === 0 && <AlertTriangle className="text-warning mr-2" />}
                  {line}
                </div>
              );
            });

            return (
              <div
                key={positionKey}
                className={cn(positionIndex < (positionAvailabilityOptions?.length ?? 0) - 1 && 'border-b pb-4')}
              >
                <div>{formattedMessage}</div>
                <ul className="list-none space-y-2">
                  {toKeyedItems(position.availabilityOptions, option => option.description).map(
                    ({ key: optionKey, item: option }, optionIndex) => (
                      <li key={optionKey} className="flex items-center space-x-2 ml-2">
                        <input
                          type="radio"
                          id={`availability-option-${positionIndex}-${optionIndex}`}
                          name={`availability-options-${positionIndex}`}
                          checked={selectedOptionIndex[positionIndex] === optionIndex}
                          value={optionIndex}
                          onChange={event => handleOptionChange(positionIndex, event.target.value)}
                          className="form-radio h-4 w-4 accent-primary hover:cursor-pointer focus:outline-none"
                          data-testid={`cart-position-${positionIndex}-${optionIndex}`}
                          disabled={cartIsProcessingPosition}
                        />
                        <label
                          htmlFor={`availability-option-${positionIndex}-${optionIndex}`}
                          className={'hover:cursor-pointer'}
                        >
                          {option.description}
                        </label>
                      </li>
                    )
                  )}
                </ul>
              </div>
            );
          }
        )}
      </ScrollArea>
      <DialogFooter className={'flex-col desktop:flex-row gap-4 desktop:gap-1'}>
        <ProgressButton
          inProgress={cartIsProcessingPosition}
          onClick={confirmUserOptionSelection}
          data-testid={'cart-confirm-button'}
        >
          {t('Bestätigen')}
        </ProgressButton>
      </DialogFooter>
    </>
  );
}
